/**
 * <DenialScoreBadge> — in-flow widget rendering an encounter's denial-risk
 * score with click-to-expand reason breakdown. Drop into BillingCaptureForm,
 * encounter sign-off, or any place a billable event is composed.
 *
 * Backend: POST /api/service-encounters/{id}/denial-score (rule-based today,
 * ML-classifier-backed once Module 1 ships).
 *
 * The badge is fail-soft: if scoring fails, it renders nothing rather than
 * blocking the parent form.
 */

import { useEffect, useState } from 'react';
import { Loader2, AlertTriangle, X, CheckCircle, ChevronDown, ChevronUp, Activity } from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';

interface DenialReason {
  code: string;
  weight: number;
  severity: 'blocker' | 'warning' | 'info' | string;
  message: string;
}

interface DenialScore {
  score: number;
  verdict: 'allow' | 'warn' | 'block';
  reasons: DenialReason[];
}

interface Props {
  encounterId: number | null | undefined;
  /** Bump this number whenever the parent form changes payload to retrigger */
  refreshKey?: number;
  /** Notify parent of latest score so it can block Submit on 'block' verdict */
  onScoreChange?: (score: DenialScore | null) => void;
  /** Skip auto-fetch and only score on demand */
  manual?: boolean;
  /** Compact pill mode (no breakdown until expanded) — default true */
  compact?: boolean;
}

function authHeaders() {
  const token = localStorage.getItem('auth_token');
  const cboId = localStorage.getItem('active_cbo_id');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(cboId && { 'X-CBO-ID': cboId }),
  };
}

export default function DenialScoreBadge({
  encounterId,
  refreshKey = 0,
  onScoreChange,
  manual = false,
  compact = true,
}: Props) {
  const [busy, setBusy] = useState(false);
  const [score, setScore] = useState<DenialScore | null>(null);
  const [expanded, setExpanded] = useState(!compact);
  const [error, setError] = useState<string | null>(null);

  async function fetchScore() {
    if (!encounterId) return;
    setBusy(true);
    setError(null);
    try {
      const r = await fetch(
        getWeaveinApiUrl(`/api/service-encounters/${encounterId}/denial-score`),
        { method: 'POST', credentials: 'include', headers: authHeaders() }
      );
      if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
      const data = (await r.json()) as DenialScore;
      setScore(data);
      onScoreChange?.(data);
    } catch (e: any) {
      setError(e.message || String(e));
      onScoreChange?.(null);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (!manual) void fetchScore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encounterId, refreshKey, manual]);

  if (!encounterId) return null;

  if (busy && !score) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-paper-2 px-2 py-0.5 text-[11px] text-ink-500">
        <Loader2 className="h-3 w-3 animate-spin" />
        Scoring…
      </span>
    );
  }

  if (error) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-paper-2 px-2 py-0.5 text-[11px] text-ink-400">
        <Activity className="h-3 w-3" />
        Score unavailable
      </span>
    );
  }

  if (!score) return null;

  const verdictMeta = {
    allow: { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: CheckCircle, label: 'Allow' },
    warn: { cls: 'bg-amber-50 text-amber-700 border-amber-200', Icon: AlertTriangle, label: 'Warn' },
    block: { cls: 'bg-red-50 text-red-700 border-red-200', Icon: X, label: 'Block' },
  }[score.verdict];
  const { cls, Icon, label } = verdictMeta;

  return (
    <div className="inline-block">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium ${cls}`}
        title="Click for denial-risk breakdown"
      >
        <Icon className="h-3 w-3" />
        Denial {label} · {(score.score * 100).toFixed(0)}%
        {compact && (expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
      </button>

      {expanded && score.reasons.length > 0 && (
        <div className="mt-2 max-w-md rounded-md border border-v2border bg-paper-2 p-2 text-xs">
          <div className="mb-1 text-[10px] uppercase tracking-wide text-ink-400">
            {score.reasons.length} risk signal{score.reasons.length === 1 ? '' : 's'}
          </div>
          <ul className="space-y-1">
            {score.reasons.slice(0, 5).map((r, i) => {
              const sevCls =
                r.severity === 'blocker'
                  ? 'text-red-600'
                  : r.severity === 'warning'
                  ? 'text-amber-600'
                  : 'text-ink-500';
              return (
                <li key={i} className="flex items-start gap-1.5">
                  <span className={`mt-0.5 ${sevCls}`}>•</span>
                  <span className="flex-1">
                    <code className="rounded bg-paper px-1 text-[10px]">{r.code}</code>{' '}
                    <span className="text-ink-500">{r.message}</span>
                  </span>
                </li>
              );
            })}
            {score.reasons.length > 5 && (
              <li className="text-[11px] italic text-ink-400">
                + {score.reasons.length - 5} more
              </li>
            )}
          </ul>
        </div>
      )}

      {manual && (
        <button
          type="button"
          onClick={fetchScore}
          disabled={busy}
          className="ml-2 text-[11px] text-v2accent hover:underline disabled:opacity-50"
        >
          {busy ? 'Scoring…' : 'Re-score'}
        </button>
      )}
    </div>
  );
}
